<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import AgentService from '@/services/agent'
import AnalyticsService from '@/services/analytics'
import ConversationsOverTimeChart from './charts/ConversationsOverTimeChart.vue'
import { useCompanyStore } from "@/stores/company";

const companyStore = useCompanyStore();

// Top Stats Data
const stats = ref([
    { title: 'Total Conversations', value: '458', subtext: '↑ All time', color: 'success' },
    { title: 'Average per Day', value: '15.8', subtext: 'Over 29 periods', color: 'muted' }
])

// Agent Performance Data
const agents = ref([])

const fetchAgents = async () => {
    try {
        const currentCompanyId = companyStore.companyId
        if (currentCompanyId) {
            const response = await AgentService.getAgentsGrid(currentCompanyId)
            if (response && response.data) {
                // Assuming response.data is the array of agents
                agents.value = response.data.map((agent, index) => ({
                    id: agent.id,
                    name: agent.name,
                    status: agent.status,
                    color: index % 2 === 0 ? 'bg-primary' : 'bg-success' // Simple alternating colors for now
                })).slice(0, 5) // Limit to top 5 for the dashboard widget
            }
        }
    } catch (error) {
        console.error('Error fetching agents:', error)
    }
}

const currentFilter = ref('Daily')
const dateRange = ref(null)
const isLoading = ref(false)
const timelineData = ref(null)
const latestTimelineStat = ref({ date: '', count: 0 })

const router = useRouter()
const viewAgentAnalytics = (agentId) => {
    // In a real scenario, use true agent UUIDs. For demo, we might need a mapping or just use what we have.
    // The sample data used "A1", "A2" but the API expects UUIDs.
    // I'll assume for now the ID is sufficient or the user will fix the IDs in the agent list later.
    // The provided API example uses a UUID: ca0d3a33... 
    // I'll update the hardcoded agents to use UUIDs for testing if needed, or just pass the ID.
    router.push({ name: 'AgentAnalyticsView', params: { agentId } })
}

const setFilter = (filter) => {
    currentFilter.value = filter
}

const fetchStats = async () => {
  try {
    isLoading.value = true
    // const user = JSON.parse(localStorage.getItem('user'))
    const currentCompanyId = companyStore.companyId;
    
    if (currentCompanyId) {
        const params = {
            period: currentFilter.value.toLowerCase()
        }
        
        if (dateRange.value && dateRange.value[0] && dateRange.value[1]) {
            params.from_date = dateRange.value[0].toISOString()
            params.until_date = dateRange.value[1].toISOString()
            stats.value[0].subtext = `${dateRange.value[0].toLocaleDateString()} - ${dateRange.value[1].toLocaleDateString()}`
        } else {
             stats.value[0].subtext = 'All time'
        }

        const [statsResponse, timelineResponse] = await Promise.all([
            AnalyticsService.getConversationsAverage(currentCompanyId, params),
            AnalyticsService.getConversationsTimeline(currentCompanyId, params)
        ])

        if (statsResponse) {
            stats.value[0].value = statsResponse.total_conversations
            stats.value[1].value = Number(statsResponse.average_conversations).toFixed(1)
            stats.value[1].subtext = `Over ${statsResponse.total_periods} periods`
            
            // Dynamic title based on filter
            const periodMap = {
                'Daily': 'Day',
                'Weekly': 'Week',
                'Monthly': 'Month',
                'Yearly': 'Year'
            }
            stats.value[1].title = `Average per ${periodMap[currentFilter.value] || 'Day'}`
        }

        if (timelineResponse && timelineResponse.data) {
             const sortedData = timelineResponse.data.sort((a, b) => new Date(a.period_date) - new Date(b.period_date))
             
             const labels = sortedData.map(item => {
                 const date = new Date(item.period_date)
                 return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
             })
             
             const counts = sortedData.map(item => item.conversation_count)
             
             timelineData.value = {
                 labels: labels,
                 datasets: [
                    {
                      label: 'Conversations',
                      data: counts,
                      borderColor: '#4285F4',
                      backgroundColor: 'rgba(66, 133, 244, 0.1)',
                      tension: 0.4,
                      fill: true,
                      pointBackgroundColor: '#4285F4',
                      pointBorderWidth: 2,
                      pointRadius: 4,
                      pointHoverRadius: 6
                    }
                 ]
             }

             if (sortedData.length > 0) {
                 const lastItem = sortedData[sortedData.length - 1]
                 latestTimelineStat.value = {
                     date: new Date(lastItem.period_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
                     count: lastItem.conversation_count
                 }
             }
        }
    }
  } catch (error) {
    console.error('Error fetching analytics:', error)
  } finally {
    isLoading.value = false
  }
}

watch([dateRange, currentFilter], () => {
    fetchStats()
})

onMounted(() => {
    fetchStats()
    fetchAgents()
})
</script>

<template>
  <div class="w-full p-4 dashboard-container">
    <div class="flex justify-between items-start mb-4 header-row">
      <div class="flex flex-col gap-1">
        <h2 class="h4 font-bold text-white mb-0 flex items-center">
          <span class="mr-2 chart-icon">📊</span> Conversations Analytics
        </h2>
        <p class="header-subtitle">Track total sessions, daily averages, and top-performing agents over time.</p>
      </div>
      <div class="flex gap-2">
         <div style="min-width: 250px;">
            <VueDatePicker v-model="dateRange" range :multi-calendars="{ solo: true }" placeholder="Select Date Range" :enable-time-picker="false" auto-apply class="date-picker-custom" />
         </div>
          <div class="btn-group" role="group">
            <button type="button" class="btn btn-sm btn-filter" :class="{ active: currentFilter === 'Daily' }" @click="setFilter('Daily')">Daily</button>
            <button type="button" class="btn btn-sm btn-filter" :class="{ active: currentFilter === 'Weekly' }" @click="setFilter('Weekly')">Weekly</button>
            <button type="button" class="btn btn-sm btn-filter" :class="{ active: currentFilter === 'Monthly' }" @click="setFilter('Monthly')">Monthly</button>
            <button type="button" class="btn btn-sm btn-filter" :class="{ active: currentFilter === 'Yearly' }" @click="setFilter('Yearly')">Yearly</button>
          </div>
      </div>
    </div>

    <!-- Top Stats Cards -->
    <div class="grid grid-cols-12 gap-3 mb-4">
      <div v-for="(stat, index) in stats" :key="index" class="col-span-12 md:col-span-6">
        <div class="card h-full border-0 shadow-sm rounded-3">
          <div class="card-body">
             <h6 class="card-subtitle mb-2 text-gray-500 small">{{ stat.title }}</h6>
             <h2 class="card-title font-bold mb-1">
                 <span v-if="isLoading && index === 0" class="spinner-sm" role="status" aria-hidden="true"></span>
                 <span v-else>{{ stat.value }}</span>
             </h2>
             <small :class="`text-${stat.color}`">{{ stat.subtext }}</small>
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-12 gap-3 mb-4">
       <!-- Conversations Over Time -->
      <div class="col-span-12 md:col-span-8">
        <div class="card border-0 shadow-sm rounded-3 h-full">
          <div class="card-body">
             <div class="flex justify-between items-start mb-3">
                 <h5 class="card-title font-bold">Conversations Over Time</h5>
                  <div class="timeline-stat-badge" v-if="latestTimelineStat.date">
                      <div class="timeline-stat-date">{{ latestTimelineStat.date }}</div>
                      <div class="timeline-stat-count">{{ latestTimelineStat.count }} convos</div>
                  </div>
             </div>
             <div style="height: 300px;">
                 <ConversationsOverTimeChart v-if="timelineData" :data="timelineData" />
                 <div v-else class="h-full flex items-center justify-center text-gray-500">
                    No data available for this period
                 </div>
             </div>
              <!-- 
              <div class="flex justify-between mt-2 small text-gray-500">
                  <span>Aug</span>
                  <span>Sep</span>
                  <span>Nov</span>
                  <span>Dec</span>
                  <span>Jan</span>
              </div> 
              -->
          </div>
        </div>
      </div>

       <!-- Agent Performance -->
       <div class="col-span-12 md:col-span-4">
            <div class="card border-0 shadow-sm rounded-3 h-full">
               <div class="card-body">
                   <h5 class="card-title font-bold mb-1">Agent Performance</h5>
                   <p class="text-gray-500 small mb-3">Top performing agents</p>
                   
                   <div class="flex flex-col gap-3">
                       <div v-for="agent in agents" :key="agent.id" class="flex items-center justify-between p-2 rounded bg-light cursor-pointer" @click="viewAgentAnalytics(agent.id)" style="cursor: pointer;">
                           <div class="flex items-center gap-3">
                               <div class="rounded-full flex items-center justify-center text-white" :class="agent.color" style="width: 40px; height: 40px; font-weight: bold;">
                                   {{ agent.name.charAt(0).toUpperCase() }}
                               </div>
                               <div class="flex flex-col">
                                   <span class="font-bold text-dark small">{{ agent.name }}</span>
                               </div>
                           </div>
                           <span class="badge bg-success-subtle text-success rounded-pill px-3 capitalize">{{ agent.status }}</span>
                       </div>
                   </div>
               </div>
           </div>
       </div>
    </div>

  </div>
</template>

<style scoped>
.dashboard-container {
    background-color: #f8f9fa; /* Light background for the dashboard area */
    min-height: 100vh;
}

/* Header Styling */
.header-subtitle { font-size: 12.5px; color: rgba(255,255,255,0.55); margin: 0; line-height: 1.4; }

.header-row {
    background-color: #1a1f36; /* Dark navy background */
    padding: 1.5rem;
    border-radius: 8px;
    margin-left: -12px;
    margin-right: -12px;
    margin-top: -12px;
}

.chart-icon {
    font-size: 1.5rem;
}

/* Button Group Styling */
.btn-filter {
    background-color: #2b324a;
    color: #fff;
    border: none;
    padding: 6px 16px;
    font-size: 0.85rem;
}

.btn-filter:hover {
    background-color: #374151;
    color: #fff;
}

.btn-filter.active {
    background-color: #4285F4;
    color: #fff;
}

.btn-group {
    background-color: #2b324a;
    border-radius: 20px;
    overflow: hidden;
    padding: 2px;
}

.btn-group .btn {
    border-radius: 18px !important;
    margin: 0 2px;
}

/* Quick Stats Colors (using standard Bootstrap colors or custom classes if defined globaly, here approximating with utility-like classes) */
.bg-blue-50 { background-color: #eff6ff; }
.bg-green-50 { background-color: #f0fdf4; }
.bg-yellow-50 { background-color: #fefce8; }
.bg-purple-50 { background-color: #faf5ff; }

.bg-success-subtle {
    background-color: #d1fae5;
    color: #065f46;
}

.text-primary { color: #4285F4 !important; }
.text-success { color: #34A853 !important; }
.text-warning { color: #db9d04 !important; } 
/* Adjusted warning color for better visibility against light bg */
.text-danger { color: #9333ea !important; } /* Using purple for 4th item to match mockup */

.card {
    border: 1px solid rgba(0,0,0,0.05);
}

:deep(.date-picker-custom .dp__input:placeholder-shown) {
    text-indent: 20px !important;
}

:deep(.date-picker-custom .dp__input_icons) {
    padding: 0 !important;
}

.card-body {
  padding: 0 !important;
}

/* ── Dark mode ─────────────────────────────────────────────── */
[data-theme="dark"] .dashboard-container {
  background-color: transparent;
}

[data-theme="dark"] .card {
  border-color: #2d3f5a;
}

[data-theme="dark"] .card-subtitle,
[data-theme="dark"] .text-gray-500 {
  color: #9aadc4 !important;
}

[data-theme="dark"] .card-title {
  color: #e8edf5;
}

[data-theme="dark"] .bg-light {
  background-color: #28364e !important;
}

[data-theme="dark"] .text-dark {
  color: #e8edf5 !important;
}

[data-theme="dark"] .bg-success-subtle {
  background-color: rgba(52, 211, 153, 0.15) !important;
  color: #34d399 !important;
}

/* Timeline latest-stat badge */
.timeline-stat-badge {
  display: inline-block;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  text-align: end;
  background-color: #1f2937;
  border: 1px solid transparent;
  color: #fff;
}

.timeline-stat-date {
  font-size: 0.75rem;
  line-height: 1.1;
  color: rgba(255, 255, 255, 0.65);
}

.timeline-stat-count {
  font-size: 0.875rem;
  font-weight: 700;
  color: #fff;
  margin-top: 2px;
}

[data-theme="dark"] .timeline-stat-badge {
  background-color: #28364e;
  border-color: #3d5070;
}

[data-theme="dark"] .timeline-stat-date {
  color: #9aadc4;
}

[data-theme="dark"] .timeline-stat-count {
  color: #e8edf5;
}

[data-theme="dark"] .text-gray-500 {
  color: #9aadc4 !important;
}

[data-theme="dark"] .text-success {
  color: #34d399 !important;
}
</style>
